import {
  Drawer,
  IconButton,
  styled,
  useTheme,
  Divider,
  List,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import React, { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import { DRAWER_WIDTH } from 'constants/sizing';
import { NavCategory } from 'constants/NavCategory';
import { ComputedRoute } from 'utils/ComputedRoute';
import { useHostSystems } from 'hooks';
import { DEFAULT_BRAND_SEARCH } from 'constants/brands';

import { NavSection } from './models';
import NavigationSection from './NavigationSection';
import messages from './messages';

interface IProps {
  open?: boolean;
  onChangeOpen?: () => void;
}

export const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
  justifyContent: 'flex-end',
}));

const Navigation = ({ open = false, onChangeOpen = () => null }: IProps) => {
  const theme = useTheme();
  const { formatMessage } = useIntl();
  const { hostSystems, getLabel } = useHostSystems();

  const [openSections, setOpenSections] = useState<Record<string, boolean>>({});

  const changeStatus = useCallback((key: string) => {
    setOpenSections((prev) => {
      const copy = { ...prev };
      copy[key] = copy[key] ? false : true;
      return copy;
    });
  }, []);

  const getHosts = useCallback(
    (cat: NavCategory) => {
      // let urlFunction =
      switch (cat) {
        case NavCategory.BRANDS:
          return hostSystems.map(
            (host): NavSection => ({
              key: host,
              label: getLabel(host),
              href: ComputedRoute.getBrandsRoute({
                hostSystems: [host],
                brandRegex: DEFAULT_BRAND_SEARCH,
              }),
            })
          );
        case NavCategory.CATEGORIES:
          return hostSystems.map(
            (host): NavSection => ({
              key: host,
              label: getLabel(host),
              href: ComputedRoute.getCategoriesRoute({
                hostSystems: [host],
              }),
            })
          );
        default:
          return undefined;
      }
    },
    [getLabel, hostSystems]
  );

  const SECTIONS: NavSection[] = useMemo(
    () => [
      // {
      //   key: NavCategory.HOME,
      //   label: formatMessage(messages.home),
      //   href: ComputedRoute.getHomeRoute(),
      // },
      {
        key: NavCategory.SEARCH,
        label: formatMessage(messages.search),
        href: ComputedRoute.getSearchRoute({}),
      },
      {
        key: NavCategory.BRANDS,
        label: formatMessage(messages.brands),
        onClick: () => changeStatus(NavCategory.BRANDS),
        subsections: getHosts(NavCategory.BRANDS),
      },
      {
        key: NavCategory.CATEGORIES,
        label: formatMessage(messages.categories),
        onClick: () => changeStatus(NavCategory.CATEGORIES),
        subsections: getHosts(NavCategory.CATEGORIES),
      },
    ],
    [changeStatus, formatMessage, getHosts]
  );

  return (
    <Drawer
      sx={{
        width: DRAWER_WIDTH,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: DRAWER_WIDTH,
          boxSizing: 'border-box',
        },
      }}
      variant='persistent'
      anchor='left'
      open={open}
    >
      <DrawerHeader>
        <IconButton onClick={onChangeOpen}>
          {theme.direction === 'ltr' ? (
            <ChevronLeftIcon />
          ) : (
            <ChevronRightIcon />
          )}
        </IconButton>
      </DrawerHeader>
      <Divider />
      <List sx={{ paddingTop: '0px', paddingBottom: '0px' }}>
        {SECTIONS.map((section) => (
          <NavigationSection
            level={0}
            open={openSections[section.key]}
            key={section.key}
            section={section}
          />
        ))}
      </List>
    </Drawer>
  );
};

export default Navigation;
